import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAdminStore } from '../../store/adminStore'
import Spinner from '../../components/common/Spinner'
import type { LeaderboardEntry } from '../../types'

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  active:    { bg: '#22c55e22', color: '#22c55e' },
  suspended: { bg: '#f59e0b22', color: '#f59e0b' },
  banned:    { bg: '#ef444422', color: '#ef4444' },
}

function LeaderboardCard({ entry }: { entry: LeaderboardEntry }) {
  const navigate = useNavigate()
  const rankColors = ['#fbbf24', '#9ca3af', '#b45309']
  const rankColor = entry.rank <= 3 ? rankColors[entry.rank - 1] : 'var(--tg-theme-text-color)'

  return (
    <div
      className="rounded-xl p-3 cursor-pointer active:opacity-70 transition-opacity flex items-center gap-3"
      style={{ background: 'var(--app-card-bg)' }}
      onClick={() => navigate(`/admin/workers/${entry.id}`)}
    >
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0"
        style={{ background: 'var(--app-page-bg)', color: rankColor }}
      >
        {entry.rank}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate" style={{ color: 'var(--tg-theme-text-color)' }}>
          {entry.first_name || entry.telegram_username || 'Unknown'}
          {entry.telegram_username && (
            <span className="opacity-50 font-normal"> @{entry.telegram_username}</span>
          )}
        </p>
        <div className="flex gap-3 mt-0.5">
          <span className="text-xs opacity-60" style={{ color: 'var(--tg-theme-text-color)' }}>
            ✓ {entry.items_verified_total} verified
          </span>
          <span className="text-xs opacity-60" style={{ color: 'var(--tg-theme-text-color)' }}>
            {entry.approval_rate}% rate
          </span>
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-xs font-medium" style={{ color: 'var(--tg-theme-link-color)' }}>
          Lv.{entry.level}
        </p>
        <p className="text-xs opacity-50" style={{ color: 'var(--tg-theme-text-color)' }}>
          {entry.coins_total_earned} 🪙
        </p>
      </div>
    </div>
  )
}

export default function AdminWorkersPage() {
  const navigate = useNavigate()
  const { workers, totalWorkers, leaderboard, isLoading, error, fetchWorkers, fetchLeaderboard } =
    useAdminStore()

  const [search, setSearch] = useState('')
  const [searchDebounce, setSearchDebounce] = useState('')
  const isFirstLoad = useRef(true)

  useEffect(() => {
    fetchLeaderboard()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounce(search), 300)
    return () => clearTimeout(t)
  }, [search])

  useEffect(() => {
    fetchWorkers({ skip: 0, search: searchDebounce || undefined }).finally(() => {
      isFirstLoad.current = false
    })
  }, [searchDebounce]) // eslint-disable-line react-hooks/exhaustive-deps

  const hasMore = workers.length < totalWorkers

  const handleLoadMore = () => {
    fetchWorkers({ skip: workers.length, search: searchDebounce || undefined }, true)
  }

  if (isLoading && isFirstLoad.current) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="p-4 space-y-4 pb-8">
      {/* Search */}
      <div
        className="flex items-center gap-2 rounded-xl px-3"
        style={{ background: 'var(--app-card-bg)' }}
      >
        <span className="opacity-40 text-sm">🔍</span>
        <input
          style={{
            background: 'transparent',
            color: 'var(--tg-theme-text-color)',
            border: 'none',
            padding: '10px 0',
            width: '100%',
            fontSize: '14px',
            outline: 'none',
          }}
          placeholder="Search workers…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button
            className="text-xl leading-none opacity-40"
            style={{ color: 'var(--tg-theme-text-color)' }}
            onClick={() => setSearch('')}
          >
            ×
          </button>
        )}
      </div>

      {/* Leaderboard */}
      {leaderboard.length > 0 && !search && (
        <div className="space-y-2">
          <p className="text-xs font-semibold opacity-50 uppercase tracking-wider" style={{ color: 'var(--tg-theme-text-color)' }}>
            Top Workers
          </p>
          {leaderboard.slice(0, 10).map((entry) => (
            <LeaderboardCard key={entry.id} entry={entry} />
          ))}
        </div>
      )}

      {/* Worker list */}
      <div className="space-y-2">
        {search && (
          <p className="text-xs opacity-50" style={{ color: 'var(--tg-theme-text-color)' }}>
            {totalWorkers} worker{totalWorkers !== 1 ? 's' : ''} found
          </p>
        )}

        {error && (
          <p className="text-sm text-center py-4" style={{ color: '#ef4444' }}>
            {error}
          </p>
        )}

        {!isLoading && workers.length === 0 && !error && (
          <div className="text-center py-12">
            <div className="text-4xl mb-3">👥</div>
            <p className="text-sm opacity-40" style={{ color: 'var(--tg-theme-text-color)' }}>
              {search ? 'No workers match your search' : 'No workers yet'}
            </p>
          </div>
        )}

        {workers.map((worker) => {
          const s = STATUS_COLORS[worker.status] ?? STATUS_COLORS.active
          return (
            <div
              key={worker.id}
              className="rounded-xl p-3 cursor-pointer active:opacity-70 transition-opacity"
              style={{ background: 'var(--app-card-bg)' }}
              onClick={() => navigate(`/admin/workers/${worker.id}`)}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium" style={{ color: 'var(--tg-theme-text-color)' }}>
                      {worker.first_name || worker.telegram_username || 'Worker'}
                    </p>
                    {worker.telegram_username && (
                      <span className="text-xs opacity-50" style={{ color: 'var(--tg-theme-text-color)' }}>
                        @{worker.telegram_username}
                      </span>
                    )}
                    <span
                      className="text-xs px-1.5 py-0.5 rounded-full font-medium"
                      style={{ background: s.bg, color: s.color }}
                    >
                      {worker.status}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                    <span className="text-xs opacity-60" style={{ color: 'var(--tg-theme-text-color)' }}>
                      Found: {worker.items_found_total}
                    </span>
                    <span className="text-xs opacity-60" style={{ color: 'var(--tg-theme-text-color)' }}>
                      Verified: {worker.items_verified_total}
                    </span>
                    <span className="text-xs opacity-60" style={{ color: 'var(--tg-theme-text-color)' }}>
                      Rate: {worker.approval_rate}%
                    </span>
                    <span className="text-xs opacity-60" style={{ color: 'var(--tg-theme-text-color)' }}>
                      Tasks: {worker.total_tasks_completed}
                    </span>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs font-semibold" style={{ color: 'var(--tg-theme-link-color)' }}>
                    Lv.{worker.level}
                  </p>
                  <p className="text-xs opacity-50" style={{ color: 'var(--tg-theme-text-color)' }}>
                    {worker.coins_total_earned} 🪙
                  </p>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {hasMore && (
        <button
          className="w-full py-2.5 rounded-xl text-sm font-medium disabled:opacity-40"
          style={{ background: 'var(--app-card-bg)', color: 'var(--tg-theme-link-color)' }}
          disabled={isLoading}
          onClick={handleLoadMore}
        >
          {isLoading ? 'Loading…' : `Load more (${totalWorkers - workers.length} remaining)`}
        </button>
      )}
    </div>
  )
}
